/**
 * Service pour les opérations en arrière-plan (non-critiques)
 * Ces opérations ne doivent jamais faire échouer les transactions principales
 */

import { logger } from '@/utils/productionLogger';
import type { ReferentStatus } from '@/types/states';

interface BackgroundOperation {
    id: string;
    type: 'audit' | 'event' | 'notification';
    operation: () => Promise<void>;
    retryCount: number;
    maxRetries: number;
    createdAt: Date;
}

class BackgroundOperationsService {
    private operations: Map<string, BackgroundOperation> = new Map();
    private isProcessing = false;
    private readonly MAX_RETRIES = 3;
    private readonly RETRY_DELAY = 1000; // 1 seconde

    /**
     * Ajoute une opération d'audit en arrière-plan
     */
    queueAuditOperation(
        entityType: 'campaign' | 'referent',
        entityId: string,
        fromState: any,
        toState: any,
        userId?: string,
        reason?: string
    ): void {
        const operationId = `audit-${Date.now()}-${Math.random()}`;

        const operation: BackgroundOperation = {
            id: operationId,
            type: 'audit',
            operation: async () => {
                const { AuditService } = await import('./AuditService');
                const auditService = new AuditService();
                await auditService.logStateTransition(entityType, entityId, fromState, toState, userId, reason);
            },
            retryCount: 0,
            maxRetries: this.MAX_RETRIES,
            createdAt: new Date()
        };

        this.operations.set(operationId, operation);
        this.processOperations();
    }

    /**
     * Ajoute une opération d'événement en arrière-plan
     */
    queueEventOperation(
        referentId: string,
        campaignId: string,
        oldStatus: ReferentStatus,
        newStatus: ReferentStatus
    ): void {
        const operationId = `event-${Date.now()}-${Math.random()}`;

        const operation: BackgroundOperation = {
            id: operationId,
            type: 'event',
            operation: async () => {
                const { eventBus } = await import('@/lib/EventBus');
                await eventBus.emit('referent-status-changed', {
                    campaignId,
                    referentId,
                    oldStatus,
                    newStatus
                });
            },
            retryCount: 0,
            maxRetries: this.MAX_RETRIES,
            createdAt: new Date()
        };

        this.operations.set(operationId, operation);
        
        // Traitement immédiat pour les événements critiques (progression de campagne)
        // Utiliser setTimeout pour éviter les problèmes de concurrence
        setTimeout(() => this.processOperations(), 0);
    }

    /**
     * Traite les opérations en arrière-plan
     */
    private async processOperations(): Promise<void> {
        if (this.isProcessing) return;

        this.isProcessing = true;

        try {
            for (const [operationId, operation] of this.operations.entries()) {
                try {
                    await operation.operation();

                    // Succès - supprimer l'opération
                    this.operations.delete(operationId);
                    logger.info(`[BackgroundOps] Operation ${operation.type} completed successfully`, {
                        operationId,
                        type: operation.type
                    });

                } catch (_error) {
                    operation.retryCount++;

                    if (operation.retryCount >= operation.maxRetries) {
                        // Max retries atteint - supprimer et logger l'échec
                        this.operations.delete(operationId);
                        logger.error(`[BackgroundOps] Operation ${operation.type} failed after ${operation.maxRetries} retries`, error as Error, {
                            operationId,
                            type: operation.type
                        });
                    } else {
                        // Programmer un retry
                        logger.warn(`[BackgroundOps] Operation ${operation.type} failed, retry ${operation.retryCount}/${operation.maxRetries}`, {
                            operationId,
                            type: operation.type,
                            error: (error as Error).message
                        });

                        // Attendre avant le prochain retry
                        await new Promise(resolve => setTimeout(resolve, this.RETRY_DELAY * operation.retryCount));
                    }
                }
            }
        } finally {
            this.isProcessing = false;
        }

        // Si il reste des opérations, programmer un nouveau traitement
        if (this.operations.size > 0) {
            setTimeout(() => this.processOperations(), this.RETRY_DELAY);
        }
    }

    /**
     * Obtient le statut des opérations en cours
     */
    getStatus(): {
        pending: number;
        byType: Record<string, number>;
    } {
        const byType: Record<string, number> = {};

        for (const operation of this.operations.values()) {
            byType[operation.type] = (byType[operation.type] || 0) + 1;
        }

        return {
            pending: this.operations.size,
            byType
        };
    }

    /**
     * Nettoie les opérations anciennes (plus de 1 heure)
     */
    cleanup(): void {
        const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);

        for (const [operationId, operation] of this.operations.entries()) {
            if (operation.createdAt < oneHourAgo) {
                this.operations.delete(operationId);
                logger.warn(`[BackgroundOps] Cleaned up old operation`, {
                    operationId,
                    type: operation.type,
                    age: Date.now() - operation.createdAt.getTime()
                });
            }
        }
    }
}

// Instance singleton
export const backgroundOperations = new BackgroundOperationsService();

// Nettoyage automatique toutes les heures
if (typeof window !== 'undefined') {
    setInterval(() => {
        backgroundOperations.cleanup();
    }, 60 * 60 * 1000); // 1 heure
}